import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

/**
 * Word-by-word reveal on a big typographic statement. The section is
 * intentionally slow and pinned-ish — feels like a chapter break, not
 * another feature callout. Copy stays warm and declarative: this is
 * what the house stands for, not what it has.
 */
export function Approach() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 85%', 'end 30%'],
  });

  // Line-by-line reveal tied to the scroll window. Three phrases:
  // each one gets a third of the range.
  const line1 = useTransform(scrollYProgress, [0, 0.25, 0.5], [0.08, 0.4, 1]);
  const line2 = useTransform(scrollYProgress, [0.2, 0.45, 0.7], [0.08, 0.4, 1]);
  const line3 = useTransform(scrollYProgress, [0.4, 0.65, 0.9], [0.08, 0.4, 1]);

  return (
    <section
      ref={ref}
      className="relative border-t border-line bg-bg py-40 sm:py-56"
      aria-label="Nossa abordagem"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-line" />
      <div className="mx-auto max-w-6xl px-6 sm:px-12 lg:px-20">
        <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-accent">
          Nossa abordagem
        </p>
        <div className="mt-10 space-y-6 font-display text-[clamp(2.25rem,7vw,5.5rem)] font-light leading-[1.1] tracking-[-0.01em] text-ink">
          <motion.p style={{ opacity: line1 }} className="max-w-[22ch]">
            Uma casa <span className="text-accent italic">não é</span> uma sequência de materiais.
          </motion.p>
          <motion.p style={{ opacity: line2 }} className="max-w-[24ch]">
            É um registro de <span className="text-accent italic">decisões</span> que amadurecem com quem mora.
          </motion.p>
          <motion.p style={{ opacity: line3 }} className="max-w-[28ch]">
            Feita com a <span className="text-accent italic">disciplina</span> de um projeto de software.
          </motion.p>
        </div>

        <motion.div
          className="mt-16 max-w-[55ch] font-serif text-sm leading-relaxed text-ink-muted sm:text-base [-letter-spacing:0.005em]"
          style={{ opacity: line3 }}
        >
          Cada etapa passa por um relatório diário, uma lista de
          verificação e uma revisão independente antes da próxima. Não é
          uma filosofia — é como a obra presta contas com ela mesma.
        </motion.div>
      </div>
    </section>
  );
}
